// Parses raw T1 synthesis docs (backend/data/memory/T1/*.md) into
// (proposition, evidence-surfaces) pairs for the Memory hub hero.
//
// Doc shape (verified against real backend fixtures):
//   ## <slot>
//   - <proposition text>. [^1], [^2] <!--m_...-->
//   ---
//   [^1]: quiz:m_...
//   [^2]: chat:m_...
//
// A bullet's evidence surfaces come from resolving its [^N] refs through the
// footnote block. Bullets may carry zero refs (scope) or comma-joined refs
// (profile) — both handled. Verified: scratchpad/parse_t1_check.mjs.

export interface Proposition {
  text: string;
  surfaces: string[]; // T2 surface keys, distinct, first-seen order
  sourceKey: string; // originating T1 slot: profile | recent | scope
}

const FOOTNOTE_DEF = /^\[\^(\d+)\]:\s*([a-z]+):/i;
const REF_TOKEN = /\[\^(\d+)\]/g;
const TRAILING_REFS = /[\s,]*(?:\[\^\d+\][\s,]*)+$/;
const HTML_COMMENT = /<!--.*?-->/g;

export function parseT1Doc(key: string, content: string): Proposition[] {
  if (!content) return [];
  const lines = content.split("\n");

  const footnotes = new Map<string, string>();
  for (const ln of lines) {
    const m = ln.match(FOOTNOTE_DEF);
    if (m) footnotes.set(m[1], m[2].toLowerCase());
  }

  const props: Proposition[] = [];
  for (const ln of lines) {
    if (!ln.startsWith("- ")) continue;
    const raw = ln.slice(2);

    const surfaces: string[] = [];
    let rm: RegExpExecArray | null;
    REF_TOKEN.lastIndex = 0;
    while ((rm = REF_TOKEN.exec(raw)) !== null) {
      const s = footnotes.get(rm[1]);
      if (s && !surfaces.includes(s)) surfaces.push(s);
    }

    const text = raw.replace(HTML_COMMENT, "").replace(TRAILING_REFS, "").trim();
    if (text) props.push({ text, surfaces, sourceKey: key });
  }
  return props;
}

// Surface key → i18n label key (English source string; ko value in app.json).
export const SURFACE_LABEL_KEY: Record<string, string> = {
  chat: "Chat",
  notebook: "Notebook",
  quiz: "Quiz",
  kb: "Knowledge map",
  book: "Course",
  partner: "Partner",
  cowriter: "Talk note",
};
